import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Col, Flex, Form, Row, Skeleton, Switch, Typography } from 'antd'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import { pickFirstViableExcelSheet } from '../pickFirstViableExcelSheet'
import { useNavigate, useParams } from 'react-router-dom'
import { getFileMappingFormat } from 'src/app/shared/services/ClientSetupService'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { colorText, colorTextSubtle } from 'src/app/themes/primaryThemeDefault'

import MappingFields from './MappingFields'
import UploadForm from './UploadForm'
import FilePreview from '../add-file-mapping/FilePreview'
import './index.scss'
import { setDateFormat } from './utils'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { csvHelper } from '../csvHelper'
import MappingFieldsTxt from '../add-file-mapping/MappingFieldsTxt'
import {
  applySkipRowsRange,
  applySkipRowsRangeToText,
  EXTRA_COLUMN,
  formatSheetData,
  getKeptColumnIndicesForRawData,
  preprocessSheetLikeDataForPreview,
} from '../add-file-mapping/utils'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'

function ViewFileMapping() {
  const { t } = useTranslation()
  const params = useParams()
  const { id } = params
  const navigate = useNavigate()
  const lookupValues = useSelector(getLookupValues)

  const { DateFormatWithSeparator, DateFormat } = lookupValues || {}

  const [fileData, setFileData] = useState<any>()
  const [columnList, setColumnList] = useState<any>()
  const [isFetched, setIsFetched] = useState(false)
  const [filePath, setFilePath] = useState('')
  const [textQualifier, setTextQualifier] = useState('')
  const [sampleFile, setSampleFile] = useState('')
  // sequential position in filtered fileData -> original 0-based column index in the file.
  const keptColumnIndicesRef = useRef<number[]>([])
  const getFileMappingInfo = async () => {
    if (id) {
      const res = await getFileMappingFormat(id)

      const checkNumberList = res?.data?.checkNumberRange?.split('-')
      const amountList = res?.data?.amountRange?.split('-')
      const issuedDateList = res?.data?.issuedDateRange?.split('-')
      const accountNumberList = res?.data?.accountNumberRange?.split('-')
      const issuedPayeeList = res?.data?.issuedPayeeRange?.split('-')

      const formattedData = {
        ...res?.data,
        skipRowsEnabled: res?.data?.skipRowsFrom != null && res?.data?.skipRowsTo != null,
        dateInFileDontIncludeSeparators:
          typeof res?.data?.issuedDateFormat === 'string' &&
          (res?.data?.issuedDateFormat?.includes('/') || res?.data?.issuedDateFormat?.includes('-')),
        status: res?.data?.status === 'Inactive',

        checkNumberFrom: checkNumberList?.[0],
        checkNumberTo: checkNumberList?.[1],
        amountFrom: amountList?.[0],
        amountTo: amountList?.[1],
        issuedDateFrom: issuedDateList?.[0],
        issuedDateTo: issuedDateList?.[1],
        accountNumberFrom: accountNumberList?.[0],
        accountNumberTo: accountNumberList?.[1],
        issuedPayeeFrom: issuedPayeeList?.[0],
        issuedPayeeTo: issuedPayeeList?.[1],
        accountNumber: res?.data?.formatType === 'txt' ? null : res?.data?.accountNumber,
        amount: res?.data?.formatType === 'txt' ? null : res?.data?.amount,
        checkNumber: res?.data?.formatType === 'txt' ? null : res?.data?.checkNumber,
        issuedDate: res?.data?.formatType === 'txt' ? null : res?.data?.issuedDate,
        issuedPayee: res?.data?.formatType === 'txt' ? null : res?.data?.issuedPayee,
        issuedDateFormat: setDateFormat(
          res?.data?.issuedDateFormat,
          (typeof res?.data?.issuedDateFormat === 'string' && res?.data?.issuedDateFormat?.includes('/')) ||
            res?.data?.issuedDateFormat?.includes('-')
            ? DateFormatWithSeparator
            : DateFormat,
        ),
      }

      setFilePath(res?.data?.sampleFilePath)
      setIsFetched(true)
      setTextQualifier(res?.data?.textQualifier)
      setSampleFile(res?.data?.sampleFile)
      return formattedData
    }
  }

  const {
    control,
    formState: { errors },
    getValues,
    setError,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getFileMappingInfo(),
  })

  const formatType = useWatch({
    control,
    name: 'formatType',
  })

  const fileContainsIssuedDate = useWatch({
    control,
    name: 'fileContainsIssuedDate',
  })

  const fileContainsHeaderRow = useWatch({
    control,
    name: 'fileContainsHeaderRow',
  })

  const dateInFileDontIncludeSeparators = useWatch({
    control,
    name: 'dateInFileDontIncludeSeparators',
  })

  const isTxt = useMemo(() => formatType === 'txt', [formatType])

  const readUploadFileData = useCallback(
    async (url: string) => {
      try {
        const isCsvFile = formatType === 'csv'

        const data = await (await fetch(url)).arrayBuffer()
        const dataBlob = new Blob([new Uint8Array(data)])
        const fileFromDB = new File([dataBlob], sampleFile, isTxt ? { type: 'text/plain;charset=utf-8' } : {})
        const dataText = await fileFromDB.text()
        // US32690 - the Row Skip Range is applied to the raw file text, before the existing parsing
        // and preamble detection, so the remaining rows keep going through the current pipeline.
        let converted: any = applySkipRowsRangeToText(dataText, getValues('skipRowsFrom'), getValues('skipRowsTo'))
        let headerColumns: any = []
        if (isCsvFile || isTxt) {
          if (isCsvFile && csvHelper(converted, getValues('textQualifier')) === false) {
            setError('sampleFile', {
              message: t(`SystemMessages:${getValues('textQualifier') == 'SINGLEQUOTE' ? 'PPC09' : 'PPC10'}`),
            })
            // props?.setLoading(false)
            return
          } else {
            converted = isCsvFile
              ? csvHelper(converted, getValues('textQualifier'))
              : converted.split(/\r\n|\n|\r/gm).map((item: any) => item.split(''))

            if (isCsvFile) {
              keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
                converted as unknown[][],
                fileContainsHeaderRow,
                false,
              )
              converted = preprocessSheetLikeDataForPreview(converted, fileContainsHeaderRow, undefined, false)
            } else {
              keptColumnIndicesRef.current = []
            }

            const dataSkipped =
              0 < converted?.length
                ? converted?.[0]
                : []

            const maxLength = Math.max(...converted.map((row: any) => row.length))
            isTxt && dataSkipped.push(...Array(EXTRA_COLUMN + maxLength - dataSkipped?.length).fill(''))

            headerColumns = dataSkipped.map((item: any, index: any) =>
              isTxt ? index + 1 : item.trim().replace(/[\n\r]*/g, ''),
            )

            const columnList = headerColumns?.map((item: any, seqIdx: any) => {
              const originalIdx = isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx
              return {
                value: originalIdx,
                label: fileContainsHeaderRow
                  ? String(item)?.includes('_blank_')
                    ? ``
                    : `${isTxt ? originalIdx + 1 : item}`
                  : `${isTxt ? originalIdx + 1 : `Column ${originalIdx + 1}`}`,
              }
            })

            if (headerColumns) {
              setColumnList(columnList)
            }
            const rowData = converted?.slice(1)

            const newRowData: any = []
            rowData.forEach((row: any) => {
              let newRow = {}
              row.forEach((cell: any, cellIndex: number) => {
                if (cell === '') {
                  row[cellIndex] = ''
                } else {
                  newRow = {
                    ...newRow,
                    [headerColumns[cellIndex]]: cell,
                  }
                }
              })

              newRowData.push(newRow)
            })
          }

          converted = converted?.slice(
            0,
            converted?.length,
          )

          if (isTxt && fileContainsHeaderRow) {
            converted.unshift(headerColumns)
          }

          setFileData(converted)
          return
        }

        const workbook = XLSX.read(data, {
          raw: isCsvFile,
          cellDates: !isCsvFile,
        })

        const { worksheet } = pickFirstViableExcelSheet(workbook, { raw: isCsvFile })

        // Convert sheet data to JSON
        const listData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          blankrows: true,
          range: 0,
          defval: '',
          raw: isCsvFile,
        })

        const sheetData: any = applySkipRowsRange(
          listData as unknown[][],
          getValues('skipRowsFrom'),
          getValues('skipRowsTo'),
        )
        keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
          sheetData as unknown[][],
          fileContainsHeaderRow,
          false,
        )
        const formattedSheetData = formatSheetData(sheetData, fileContainsHeaderRow)

        setFileData(formattedSheetData)

        const columnList = formattedSheetData?.[0]?.map((item: any, seqIdx: any) => {
          const originalIdx = keptColumnIndicesRef.current[seqIdx] ?? seqIdx
          return {
            value: fileContainsHeaderRow ? item : `Column ${originalIdx + 1}`,
            label: fileContainsHeaderRow
              ? String(item)?.includes('_blank_')
                ? ``
                : item
              : `Column ${originalIdx + 1}`,
          }
        })

        setColumnList(columnList)
      } catch (e) {
        // Handle read file error
      }
    },
    [fileContainsHeaderRow, formatType],
  )

  const handleClickCancel = () => {
    navigate(menuUrls.fileMappingSetup)
  }

  useEffect(() => {
    if (filePath) readUploadFileData(filePath)
  }, [filePath, readUploadFileData])

  const columnDefs = useMemo(() => {
    const resolveOriginalIdx = (seqIdx: number) =>
      isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx

    if (fileContainsHeaderRow && fileData)
      return fileData?.[0]?.map((item: any, seqIdx: any) => {
        const originalIdx = resolveOriginalIdx(seqIdx)
        return {
          colId: originalIdx,
          field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
          headerName: String(item)?.includes('_blank_') ? '' : String(item),
          minWidth: 40,
          sortable: false,
        }
      })

    return fileData?.[0]?.map((item: any, seqIdx: any) => {
      const originalIdx = resolveOriginalIdx(seqIdx)
      return {
        colId: originalIdx,
        field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        headerName: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        minWidth: 40,
        sortable: false,
      }
    })
  }, [fileContainsHeaderRow, fileData])

  return (
    <Form name='file-mapping' className='view-file-mapping-setup-form-container read-only' layout='vertical'>
      {isFetched ? (
        <>
          <Flex className='file-mapping-setup-form-wrapper' style={{ flexDirection: 'column' }}>
            <UploadForm errors={errors} control={control} />

            <FilePreview
              fileContainsHeaderRow={fileContainsHeaderRow}
              fileData={fileData}
              columnDefs={columnDefs}
              isTxtFile={isTxt}
            />
            {isTxt ? (
              <MappingFieldsTxt
                control={control}
                fileContainsIssuedDate={fileContainsIssuedDate}
                dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                getValues={getValues}
                errors={errors}
                disabled
              />
            ) : (
              <MappingFields
                control={control}
                fileContainsIssuedDate={fileContainsIssuedDate}
                dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                columnList={columnList}
                getValues={getValues}
              />
            )}

            <Row gutter={24} align='middle' style={{ marginTop: 24 }}>
              <Col span={12}>
                <Form.Item>
                  <Controller
                    name='status'
                    control={control}
                    render={({ field }) => (
                      <Flex gap={12}>
                        <Typography style={{ color: colorText, fontWeight: 700, fontSize: 14 }}>
                          {t('FileMappingSetup:addFileMapping.label.status')}
                        </Typography>
                        <Switch {...field} checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} />
                        <Typography style={{ color: colorTextSubtle, fontSize: 16 }}>
                          {t('FileMappingSetup:addFileMapping.label.inactive')}
                        </Typography>
                      </Flex>
                    )}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
          </Flex>

          <div className='button-container'>
            <Flex className='button-group' justify='flex-end' gap={24}>
              <Button className='cancel-button' onClick={handleClickCancel}>
                {t('OverallFunctionality:cancel_btn')}
              </Button>
            </Flex>
          </div>
        </>
      ) : (
        <Skeleton />
      )}
    </Form>
  )
}

export default ViewFileMapping
